// Composition root: wires the replay engine, chart, trading sim, data import,
// and controls. Phase 4 adds the persistent journal + stats panel.

import './app.css'
import { registerSW } from 'virtual:pwa-register'
import { createBus, type ReplayEvents } from './core/events'
import { ReplayEngine } from './core/replayEngine'
import { createChartView } from './chart/chartView'
import { clampStart, adaptiveStartOpts } from './core/randomStart'
import { resample, subBarsByDisplay, indexAtOrBefore } from './core/resample'
import { TF_SECONDS, TF_LABEL, type Bar, type Timeframe } from './core/types'
import { genSyntheticBars } from './data/synth'
import { createDatasetGateway } from './data/datasetGateway'
import { parseOhlcCsv } from './data/csvImport'
import { guessSymbol } from './data/symbolGuess'
import { SimEngine } from './sim/SimEngine'
import { INSTRUMENTS, getInstrument } from './sim/instruments'
import { sizeByRisk, tpFromR } from './sim/sizing'
import type { SimEvent, Trade, Side } from './sim/types'
import { createJournalStore } from './journal/JournalStore'
import { computeStats } from './stats/StatsEngine'

const SIZE = 0.1
const SPEEDS = [0.5, 1, 2, 5, 10, 30, 100]
const ALL_TFS: Timeframe[] = ['m1', 'm5', 'm15', 'h1', 'h4', 'd1']

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag)
  if (cls) n.className = cls
  if (text) n.textContent = text
  return n
}

async function boot() {
  const appEl = document.getElementById('app')!
  const chartEl = document.getElementById('chart')!
  const controlsEl = document.getElementById('controls')!

  const bus = createBus<ReplayEvents>()
  const engine = new ReplayEngine({ bus })
  const chart = createChartView(chartEl)
  const gateway = createDatasetGateway()
  await gateway.init()
  const journal = createJournalStore()
  await journal.init()
  const ACCOUNT_BALANCE = 10000

  // ---- seed bundled real data on first run, else synthetic demo ----
  // public/data/{symbol}.json (columnar 1m) is fetched once and stored in
  // IndexedDB, so a fresh install opens on real data and works offline after.
  async function seedFromBundle(): Promise<boolean> {
    try {
      const base = import.meta.env.BASE_URL
      const res = await fetch(`${base}data/manifest.json`, { cache: 'no-cache' })
      if (!res.ok) return false
      const man = (await res.json()) as {
        datasets: { symbol: string; file: string; baseTf: Timeframe }[]
      }
      let seeded = 0
      for (const d of man.datasets) {
        const r = await fetch(`${base}data/${d.file}`)
        if (!r.ok) continue
        const cols = (await r.json()) as {
          symbol: string
          baseTf: Timeframe
          t: number[]
          o: number[]
          h: number[]
          l: number[]
          c: number[]
        }
        const bars: Bar[] = cols.t.map((time, i) => ({
          time,
          open: cols.o[i]!,
          high: cols.h[i]!,
          low: cols.l[i]!,
          close: cols.c[i]!,
          volume: 0,
        }))
        if (!bars.length) continue
        await gateway.saveDataset({ symbol: cols.symbol, source: `bundle:${d.file}`, bars, baseTf: cols.baseTf })
        seeded++
      }
      return seeded > 0
    } catch {
      return false // offline first run -> fall back to demo, retry next load
    }
  }

  let symbol = 'XAUUSD'
  let baseBars: Bar[]
  let baseTf: Timeframe
  let source: string
  let active = await gateway.getActive()
  if (!active && (await gateway.listDatasets()).length === 0) {
    const loader = el('div', undefined, '実データを準備中…（初回のみ）')
    loader.id = 'boot'
    document.body.append(loader)
    try {
      if (await seedFromBundle()) active = await gateway.getActive()
    } finally {
      loader.remove()
    }
  }
  if (active) {
    symbol = active.meta.symbol
    baseBars = active.bars
    baseTf = active.meta.baseTf
    source = 'real'
  } else {
    // 1m-based demo (offline first run / no bundle): intraday chips (5m/15m)
    // and intrabar fills out of the box. An h1 base can't be downsampled to 15m.
    baseBars = genSyntheticBars({
      count: 43200, // ~30 days of 1m -> surfaces m1/m5/m15/h1/h4/d1 chips
      stepSeconds: 60,
      startPrice: 2000,
      volatility: 0.0006,
      seed: 7,
    })
    baseTf = 'm1'
    source = 'demo'
  }

  const datasetKey = active ? active.meta.id : 'demo'
  const savedDrawings = await gateway.getDrawings(datasetKey)

  let sim = new SimEngine({ account: { startingBalance: ACCOUNT_BALANCE }, instruments: INSTRUMENTS })
  let currentTf: Timeframe = baseTf
  let currentBars: Bar[] = [] // active TF series, for date→index jumps
  let subBars: Bar[][] = [] // base (1m) bars per display bar, for intrabar fills

  // ---- top bar (symbol/TF/import) ----
  const topbar = el('div')
  topbar.id = 'topbar'
  const metaLabel = el('span', 'meta')
  const datasetSelect = el('select')
  const tfBar = el('div', 'tf-bar')
  const rangeBtn = el('button', '', '📅')
  const drawBtn = el('button', '', '✏️')
  const journalBtn = el('button', '', '📓')
  const importBtn = el('button', '', '📁 CSV')
  const fileInput = el('input')
  fileInput.type = 'file'
  fileInput.accept = '.csv,text/csv'
  fileInput.style.display = 'none'
  topbar.append(metaLabel, tfBar, el('span', 'spacer'), datasetSelect, rangeBtn, drawBtn, journalBtn, importBtn, fileInput)
  appEl.insertBefore(topbar, chartEl)

  // dataset switcher (shown once at least one CSV is imported)
  const datasets = await gateway.listDatasets()
  if (datasets.length) {
    for (const ds of datasets) {
      const o = el('option', '', `${ds.symbol} ${TF_LABEL[ds.baseTf]}`)
      o.value = ds.id
      datasetSelect.append(o)
    }
    datasetSelect.value = active?.meta.id ?? datasets[datasets.length - 1]!.id
    datasetSelect.style.display = ''
  } else {
    datasetSelect.style.display = 'none'
  }
  datasetSelect.onchange = async () => {
    await gateway.setActive(datasetSelect.value)
    location.reload()
  }

  // ---- control bar ----
  const stepBackBtn = el('button', 'btn', '⏮')
  const playBtn = el('button', 'btn', '▶')
  const stepBtn = el('button', 'btn', '⏭')
  const speedBtn = el('button', 'btn speed-chip', '1x')
  const buyBtn = el('button', 'btn buy', 'Buy')
  const sellBtn = el('button', 'btn sell', 'Sell')
  const closeBtn = el('button', 'btn close', 'Close')
  const pnl = el('span', 'pnl', '—')
  const left = el('div', 'ctrl-group')
  left.append(stepBackBtn, playBtn, stepBtn, speedBtn)
  const center = el('div', 'ctrl-group spacer')
  center.append(pnl, closeBtn)
  const right = el('div', 'ctrl-group')
  right.append(buyBtn, sellBtn)
  controlsEl.append(left, center, right)

  let speedIdx = 1
  const refreshPlay = () => (playBtn.textContent = engine.isPlaying ? '⏸' : '▶')
  const fmt = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}`

  function refreshMeta() {
    metaLabel.innerHTML = `${symbol}<span class="tag">${source === 'real' ? '実' : 'demo'}</span>`
    document.title = `${symbol} ${TF_LABEL[currentTf]}`
  }

  let lastPosKey = ''
  function refreshPositionLines() {
    const pos = sim.getPosition(symbol)
    const key = pos ? `${pos.id}:${pos.avgEntryPrice}:${pos.tp}:${pos.sl}` : ''
    if (key === lastPosKey) return
    lastPosKey = key
    chart.clearPriceLines()
    if (pos) {
      chart.addPriceLine({ price: pos.avgEntryPrice, color: '#9aa4b2', title: `${pos.direction === 'long' ? 'L' : 'S'}${pos.size}` })
      if (pos.tp) chart.addPriceLine({ price: pos.tp, color: '#26a69a', title: 'TP', dashed: true })
      if (pos.sl) chart.addPriceLine({ price: pos.sl, color: '#ef5350', title: 'SL', dashed: true })
    }
  }

  function refreshPnl() {
    const st = sim.getAccountState(symbol)
    const pos = sim.getPosition(symbol)
    if (pos) {
      pnl.textContent = `${pos.direction === 'long' ? 'L' : 'S'}${pos.size} @${pos.avgEntryPrice.toFixed(2)}  ${fmt(st.unrealized)}`
      pnl.className = 'pnl ' + (st.unrealized >= 0 ? 'pos' : 'neg')
      closeBtn.classList.add('show')
    } else {
      pnl.textContent = `Flat  Bal ${st.balance.toFixed(2)}`
      pnl.className = 'pnl'
      closeBtn.classList.remove('show')
    }
    refreshPositionLines()
  }

  async function persistClose(trade: Trade) {
    await journal.addTrade({
      ...trade,
      timeframe: currentTf,
      source,
      createdAt: Math.floor(Date.now() / 1000),
      hasSnapshot: true,
    })
    // capture the chart at close as the journal screenshot (best-effort)
    try {
      chart.takeScreenshot().toBlob((blob) => {
        if (blob) void journal.saveSnapshot(trade.id, blob)
      }, 'image/png')
    } catch {
      /* screenshot is best-effort */
    }
    if (panel.classList.contains('open')) void renderPanel()
  }

  // markTime aligns markers to the visible (display-TF) bar grid, even when
  // fills resolve on finer intrabar (1m) timestamps.
  function applyEvents(events: SimEvent[], markTime: number) {
    for (const ev of events) {
      if (ev.kind === 'close') {
        void persistClose(ev.trade)
        chart.addMarker({
          time: markTime,
          position: 'aboveBar',
          color: ev.trade.netPnL >= 0 ? '#26a69a' : '#ef5350',
          shape: 'square',
          text: fmt(ev.trade.netPnL),
        })
      }
    }
    const pos = sim.getPosition(symbol)
    const fill = events.find((e) => e.kind === 'fill')
    if (fill && fill.kind === 'fill' && pos && (pos.direction === 'long') === (fill.side === 'buy')) {
      chart.addMarker({
        time: markTime,
        position: fill.side === 'buy' ? 'belowBar' : 'aboveBar',
        color: fill.side === 'buy' ? '#26a69a' : '#ef5350',
        shape: fill.side === 'buy' ? 'arrowUp' : 'arrowDown',
        text: `${fill.side} ${fill.size}`,
      })
    }
  }

  // ---- engine -> chart + sim (registered once; read live `sim`/`symbol`) ----
  bus.on('loaded', () => {
    chart.setData(engine.visibleBars)
    const cb = engine.currentBar
    if (cb) sim.onNewBar(symbol, cb)
    refreshPnl()
  })
  bus.on('bar:revealed', (p) => {
    chart.appendBar(p.bar)
    // Intrabar fills: feed the underlying base (1m) bars in order so TP/SL
    // resolve at true sub-bar timing, while we still step on the display TF.
    const subs = subBars[p.index] ?? [p.bar]
    const events: SimEvent[] = []
    for (const sb of subs) events.push(...sim.onNewBar(symbol, sb))
    applyEvents(events, p.bar.time)
    refreshPnl()
  })
  bus.on('bar:hidden', () => {
    chart.setData(engine.visibleBars)
    refreshPnl()
  })
  bus.on('seek', () => {
    chart.setData(engine.visibleBars)
    refreshPnl()
  })
  bus.on('play', refreshPlay)
  bus.on('pause', refreshPlay)
  bus.on('end', refreshPlay)

  // ---- session (re)start at a given timeframe ----
  function tfBarsFor(tf: Timeframe): Bar[] {
    return tf === baseTf ? baseBars : resample(baseBars, tf)
  }

  function startSession(tf: Timeframe, opts: { startIndex?: number } = {}) {
    currentTf = tf
    try {
      localStorage.setItem('lastTf', tf)
    } catch {
      /* private mode */
    }
    const bars = tfBarsFor(tf)
    currentBars = bars
    subBars = subBarsByDisplay(baseBars, bars, TF_SECONDS[tf])
    sim = new SimEngine({ account: { startingBalance: ACCOUNT_BALANCE }, instruments: INSTRUMENTS })
    chart.clearMarkers()
    chart.clearPriceLines()
    lastPosKey = ''
    chart.setAutoFollow(true)
    let start: number
    if (opts.startIndex != null) {
      start = Math.max(1, Math.min(opts.startIndex, bars.length - 1))
    } else {
      const o = adaptiveStartOpts(bars.length)
      start = clampStart(o.minWarmup, bars.length, o)
    }
    engine.loadDataset(bars, { startIndex: start })
    refreshMeta()
    updateTfActive()
    refreshPlay()
  }

  // ---- TF selector ----
  function rebuildTfOptions() {
    const baseSec = TF_SECONDS[baseTf]
    const opts = ALL_TFS.filter((tf) => TF_SECONDS[tf] >= baseSec).filter(
      (tf) => tfBarsFor(tf).length >= 25,
    )
    tfBar.innerHTML = ''
    for (const tf of opts) {
      const b = el('button', 'tf-chip', TF_LABEL[tf])
      b.dataset.tf = tf
      b.onclick = () => startSession(tf)
      tfBar.append(b)
    }
    updateTfActive()
  }
  function updateTfActive() {
    for (const b of Array.from(tfBar.children) as HTMLElement[]) {
      b.classList.toggle('active', b.dataset.tf === currentTf)
    }
  }

  // ---- import ----
  importBtn.onclick = () => fileInput.click()
  fileInput.onchange = async () => {
    const file = fileInput.files?.[0]
    if (!file) return
    try {
      const text = await file.text()
      const r = parseOhlcCsv(text)
      if (!r.bars.length) {
        alert('CSVから有効なバーを読めませんでした')
        return
      }
      const sym = guessSymbol(file.name)
      await gateway.saveDataset({ symbol: sym, source: `csv:${file.name}`, bars: r.bars })
      location.reload()
    } catch (e) {
      alert('読み込みエラー: ' + (e as Error).message)
    }
  }

  // ---- controls -> engine / sim ----
  const followAnd = (fn: () => void) => {
    chart.setAutoFollow(true)
    fn()
  }
  stepBtn.onclick = () => followAnd(() => engine.step())
  stepBackBtn.onclick = () => followAnd(() => engine.stepBack())
  playBtn.onclick = () =>
    engine.isPlaying ? engine.pause() : followAnd(() => engine.play(SPEEDS[speedIdx]))
  speedBtn.onclick = () => {
    speedIdx = (speedIdx + 1) % SPEEDS.length
    const s = SPEEDS[speedIdx]!
    speedBtn.textContent = `${s}x`
    engine.setSpeed(s)
  }
  // ---- order ticket: risk-% sizing OR fixed-lot, mode toggle remembered ----
  type OrderMode = 'risk' | 'fixed'
  let orderMode: OrderMode = 'risk'
  let lastSize = SIZE
  let lastRiskPct = 2
  let lastSlDist = 0 // remembered SL distance (price units) to prefill next ticket
  let lastTpR = 2
  let fixedLot = SIZE
  let ticketSide: Side = 'buy'
  try {
    const m = localStorage.getItem('orderMode')
    if (m === 'risk' || m === 'fixed') orderMode = m
  } catch {
    /* private mode */
  }
  const curSpec = () => getInstrument(symbol)

  const ticket = el('div')
  ticket.id = 'ticket'
  const tTitle = el('div', 't-title')

  const modeBar = el('div', 't-mode')
  const riskTab = el('button', 't-tab', 'リスク%')
  const fixedTab = el('button', 't-tab', '固定ロット')
  modeBar.append(riskTab, fixedTab)

  const lab = (text: string, input: HTMLElement) => {
    const l = el('label', '', text)
    l.append(input)
    return l
  }
  const numInput = (step: string) => {
    const i = el('input')
    i.type = 'number'
    i.step = step
    i.inputMode = 'decimal'
    return i
  }

  // risk-mode fields: risk %, SL price, TP as R-multiple
  const riskFields = el('div', 't-fields')
  const riskPctInput = numInput('0.1')
  riskPctInput.min = '0'
  const slPriceInput = numInput('0.01')
  const tpRInput = numInput('0.1')
  tpRInput.min = '0'
  riskFields.append(lab('リスク %', riskPctInput), lab('SL価格', slPriceInput), lab('TP (R倍)', tpRInput))

  // fixed-mode fields: lot chips + custom, optional SL/TP price
  const fixedWrap = el('div', 't-fixed')
  const chipRow = el('div', 'lot-chips')
  const customLot = numInput('0.01')
  customLot.min = '0'
  customLot.placeholder = 'lot'
  const fixedFields = el('div', 't-fields')
  const slFixedInput = numInput('0.01')
  const tpFixedInput = numInput('0.01')
  fixedFields.append(lab('SL価格(任意)', slFixedInput), lab('TP価格(任意)', tpFixedInput))
  fixedWrap.append(chipRow, fixedFields)

  const readout = el('div', 't-readout')

  const actions = el('div', 't-actions')
  const cancelBtn = el('button', 'cancel', 'キャンセル')
  const confirmBtn = el('button', 'confirm', '確定')
  actions.append(cancelBtn, confirmBtn)
  ticket.append(tTitle, modeBar, riskFields, fixedWrap, readout, actions)
  appEl.append(ticket)

  function buildLotChips() {
    const spec = curSpec()
    const presets = Array.from(new Set([spec.minLot, 0.1, 0.5, 1].filter((v) => v >= spec.minLot)))
    chipRow.innerHTML = ''
    for (const v of presets) {
      const c = el('button', 'lot-chip', String(v))
      c.dataset.lot = String(v)
      c.onclick = () => {
        fixedLot = v
        customLot.value = ''
        updateLotChips()
        updateReadout()
      }
      chipRow.append(c)
    }
    chipRow.append(customLot)
    updateLotChips()
  }
  function updateLotChips() {
    for (const c of Array.from(chipRow.querySelectorAll('.lot-chip')) as HTMLElement[]) {
      c.classList.toggle('active', !customLot.value && Number(c.dataset.lot) === fixedLot)
    }
  }
  function currentFixedLot(): number {
    const c = parseFloat(customLot.value)
    return Number.isFinite(c) && c > 0 ? c : fixedLot
  }

  function updateReadout() {
    const entry = engine.currentBar?.close ?? 0
    const spec = curSpec()
    const balance = sim.getAccountState(symbol).balance
    if (orderMode === 'risk') {
      const slPrice = parseFloat(slPriceInput.value)
      const riskPct = parseFloat(riskPctInput.value) || 0
      if (!Number.isFinite(slPrice)) {
        readout.textContent = 'SL価格を入れるとロット自動計算'
        readout.className = 't-readout muted'
        return
      }
      const r = sizeByRisk({ balance, riskPct, entryPrice: entry, slPrice, side: ticketSide, spec })
      if (!r.valid) {
        readout.textContent = r.reason ?? '入力を確認'
        readout.className = 't-readout warn'
        return
      }
      const rr = parseFloat(tpRInput.value)
      const profit = Number.isFinite(rr) && rr > 0 ? r.lossAtSl * rr : null
      readout.innerHTML =
        `ロット <b>${r.lots}</b> ・ 損失 <span class="neg">-$${r.lossAtSl.toFixed(0)}</span>` +
        (profit !== null ? ` ・ 利益 <span class="pos">+$${profit.toFixed(0)}</span> (${rr}R)` : '')
      readout.className = 't-readout'
    } else {
      const lots = currentFixedLot()
      const slPrice = parseFloat(slFixedInput.value)
      let txt = `ロット <b>${lots}</b>`
      if (Number.isFinite(slPrice)) {
        const dist = ticketSide === 'buy' ? entry - slPrice : slPrice - entry
        if (dist > 0) {
          const loss = dist * spec.valuePerPointPerLot * lots
          txt += ` ・ 損失 <span class="neg">-$${loss.toFixed(0)}</span> (≈${((loss / balance) * 100).toFixed(1)}%)`
        }
      }
      readout.innerHTML = txt
      readout.className = 't-readout'
    }
  }

  function applyMode() {
    riskTab.classList.toggle('active', orderMode === 'risk')
    fixedTab.classList.toggle('active', orderMode === 'fixed')
    riskFields.style.display = orderMode === 'risk' ? '' : 'none'
    fixedWrap.style.display = orderMode === 'fixed' ? '' : 'none'
    updateReadout()
  }
  function setMode(m: OrderMode) {
    orderMode = m
    try {
      localStorage.setItem('orderMode', m)
    } catch {
      /* private mode */
    }
    applyMode()
  }
  riskTab.onclick = () => setMode('risk')
  fixedTab.onclick = () => setMode('fixed')
  for (const inp of [riskPctInput, slPriceInput, tpRInput, slFixedInput, tpFixedInput]) {
    inp.oninput = updateReadout
  }
  customLot.oninput = () => {
    updateLotChips()
    updateReadout()
  }

  function openTicket(side: Side) {
    ticketSide = side
    const price = engine.currentBar?.close ?? 0
    tTitle.textContent = `${side === 'buy' ? '買い (Long)' : '売り (Short)'} ${symbol} @ ${price.toFixed(2)}`
    riskPctInput.value = String(lastRiskPct)
    tpRInput.value = lastTpR ? String(lastTpR) : ''
    slPriceInput.value =
      lastSlDist > 0 ? (side === 'buy' ? price - lastSlDist : price + lastSlDist).toFixed(2) : ''
    fixedLot = lastSize
    customLot.value = ''
    slFixedInput.value = ''
    tpFixedInput.value = ''
    buildLotChips()
    confirmBtn.className = 'confirm ' + side
    confirmBtn.textContent = side === 'buy' ? '買い 確定' : '売り 確定'
    applyMode()
    ticket.classList.add('open')
  }
  cancelBtn.onclick = () => ticket.classList.remove('open')
  confirmBtn.onclick = () => {
    const price = engine.currentBar?.close ?? 0
    const markTime = engine.currentBar?.time ?? 0
    if (orderMode === 'risk') {
      const balance = sim.getAccountState(symbol).balance
      const slPrice = parseFloat(slPriceInput.value)
      const riskPct = parseFloat(riskPctInput.value)
      if (!Number.isFinite(slPrice)) {
        alert('SL価格を入力してください')
        return
      }
      const r = sizeByRisk({ balance, riskPct: riskPct || 0, entryPrice: price, slPrice, side: ticketSide, spec: curSpec() })
      if (!r.valid) {
        alert(r.reason ?? '入力を確認してください')
        return
      }
      const rr = parseFloat(tpRInput.value)
      const tp = Number.isFinite(rr) && rr > 0 ? tpFromR(ticketSide, price, r.slDistance, rr) : undefined
      lastRiskPct = riskPct || lastRiskPct
      lastSlDist = r.slDistance
      lastTpR = Number.isFinite(rr) && rr > 0 ? rr : 0
      applyEvents(sim.placeMarket(symbol, ticketSide, r.lots, { tp, sl: slPrice }), markTime)
    } else {
      const lots = currentFixedLot()
      lastSize = lots
      const slP = parseFloat(slFixedInput.value)
      const tpP = parseFloat(tpFixedInput.value)
      const sl = Number.isFinite(slP) ? slP : undefined
      const tp = Number.isFinite(tpP) ? tpP : undefined
      applyEvents(sim.placeMarket(symbol, ticketSide, lots, { tp, sl }), markTime)
    }
    refreshPnl()
    ticket.classList.remove('open')
  }

  buyBtn.onclick = () => openTicket('buy')
  sellBtn.onclick = () => openTicket('sell')
  closeBtn.onclick = () => {
    applyEvents(sim.closePosition(symbol), engine.currentBar?.time ?? 0)
    refreshPnl()
  }

  // ---- journal / stats panel ----
  const panel = document.getElementById('panel')!
  function downloadBlob(name: string, blob: Blob) {
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = name
    a.click()
    setTimeout(() => URL.revokeObjectURL(a.href), 1000)
  }
  function statCell(k: string, v: string, cls?: string): HTMLElement {
    const c = el('div', 'stat')
    c.append(el('div', 'k', k), el('div', 'v' + (cls ? ' ' + cls : ''), v))
    return c
  }
  async function renderPanel() {
    const trades = await journal.getTrades()
    const s = computeStats(trades, ACCOUNT_BALANCE)
    panel.innerHTML = ''

    const head = el('div', 'panel-head')
    const closeX = el('button', '', '✕')
    closeX.onclick = () => panel.classList.remove('open')
    head.append(el('h2', '', 'ジャーナル / 統計'), closeX)

    const body = el('div', 'panel-body')
    const grid = el('div', 'stats-grid')
    grid.append(
      statCell('純損益', s.netPnL.toFixed(2), s.netPnL >= 0 ? 'pos' : 'neg'),
      statCell('勝率', `${(s.winRate * 100).toFixed(1)}%`),
      statCell('PF', s.profitFactor === null ? '—' : String(s.profitFactor)),
      statCell('期待値', s.expectancy.toFixed(2), s.expectancy >= 0 ? 'pos' : 'neg'),
      statCell('最大DD', `${s.maxDrawdown.toFixed(0)} (${s.maxDrawdownPct}%)`),
      statCell('トレード数', String(s.totalTrades)),
      statCell('平均R', s.expectancyR === null ? '—' : String(s.expectancyR)),
      statCell('連勝/連敗', `${s.maxWinStreak}/${s.maxLossStreak}`),
      statCell('平均勝/負', `${s.avgWin.toFixed(0)}/${s.avgLoss.toFixed(0)}`),
    )
    body.append(grid)

    if (!trades.length) {
      body.append(el('div', 'empty', 'まだトレードがありません'))
    } else {
      const list = el('div')
      for (const t of [...trades].reverse()) {
        const row = el('div', 'trade-row')
        const info = el('div')
        info.append(
          el('div', '', `${t.symbol} ${t.direction === 'long' ? '▲L' : '▼S'} ${t.size} · ${t.timeframe}`),
          el('div', 'sub', `${new Date(t.exitTime * 1000).toLocaleString()} · ${t.exitReason}`),
        )
        row.append(info, el('div', 'net ' + (t.netPnL >= 0 ? 'pos' : 'neg'), fmt(t.netPnL)))
        list.append(row)
      }
      body.append(list)
    }

    const foot = el('div', 'panel-foot')
    const ej = el('button', '', 'JSON')
    ej.onclick = async () =>
      downloadBlob('journal.json', new Blob([await journal.exportJSON()], { type: 'application/json' }))
    const ec = el('button', '', 'CSV')
    ec.onclick = async () =>
      downloadBlob('journal.csv', new Blob([await journal.exportCSV()], { type: 'text/csv' }))
    const clr = el('button', 'danger', '全削除')
    clr.onclick = async () => {
      if (confirm('ジャーナルを全削除しますか？')) {
        await journal.clear()
        await renderPanel()
      }
    }
    foot.append(ej, ec, clr)

    panel.append(head, body, foot)
  }
  journalBtn.onclick = async () => {
    await renderPanel()
    panel.classList.add('open')
  }

  // ---- period / start-date jump sheet ----
  const fmtD = (sec: number) => new Date(sec * 1000).toISOString().slice(0, 10)
  const fmtDT = (sec: number) => new Date(sec * 1000).toISOString().slice(0, 16).replace('T', ' ')
  const rangeSheet = el('div')
  rangeSheet.id = 'range'
  const rTitle = el('div', 't-title', '期間 / ジャンプ')
  const rInfo = el('div', 'r-info')
  const rDateLabel = el('label', '', '開始日 (UTC)')
  const rDate = el('input')
  rDate.type = 'date'
  rDateLabel.append(rDate)
  const rActions = el('div', 't-actions')
  const rClose = el('button', 'cancel', '閉じる')
  const rRandom = el('button', 'cancel', '🎲 ランダム')
  const rJump = el('button', 'confirm buy', 'この日から開始')
  rActions.append(rClose, rRandom, rJump)
  rangeSheet.append(rTitle, rInfo, rDateLabel, rActions)
  appEl.append(rangeSheet)

  function openRange() {
    const first = currentBars[0]?.time ?? 0
    const last = currentBars.at(-1)?.time ?? 0
    const cur = engine.currentBar?.time ?? first
    rInfo.innerHTML = `データ範囲 <b>${fmtD(first)}</b> 〜 <b>${fmtD(last)}</b><br>現在地 ${fmtDT(cur)}`
    rDate.min = fmtD(first)
    rDate.max = fmtD(last)
    rDate.value = fmtD(cur)
    rangeSheet.classList.add('open')
  }
  rangeBtn.onclick = openRange
  rClose.onclick = () => rangeSheet.classList.remove('open')
  rRandom.onclick = () => {
    rangeSheet.classList.remove('open')
    startSession(currentTf)
  }
  rJump.onclick = () => {
    if (!rDate.value) return
    const sec = Math.floor(new Date(rDate.value).getTime() / 1000)
    const idx = Math.max(0, indexAtOrBefore(currentBars, sec))
    rangeSheet.classList.remove('open')
    startSession(currentTf, { startIndex: idx })
  }

  // ---- drawing tools (lightweight-charts-drawing), persisted per dataset ----
  // Curated subset of the 68 tools; click a tool then draw on the chart. The
  // library handles interactive placement + drag-editing; we persist export JSON.
  const DRAW_TOOLS: { type: string; label: string }[] = [
    { type: 'trend-line', label: '╱ トレンド' },
    { type: 'horizontal-line', label: '— 水平線' },
    { type: 'horizontal-ray', label: '→ 水平レイ' },
    { type: 'ray', label: '↗ レイ' },
    { type: 'extended-line', label: '⇿ 延長線' },
    { type: 'parallel-channel', label: '▤ チャネル' },
    { type: 'fib-retracement', label: 'fib フィボ' },
    { type: 'rectangle', label: '▭ 矩形' },
    { type: 'long-position', label: '▲ ロング' },
    { type: 'short-position', label: '▼ ショート' },
    { type: 'brush', label: '✎ ブラシ' },
    { type: 'text-annotation', label: 'T テキスト' },
  ]
  let activeTool: string | null = null

  const drawBar = el('div')
  drawBar.id = 'drawbar'
  const selectBtn = el('button', 'draw-tool', '⤧ 選択')
  drawBar.append(selectBtn)
  const toolButtons = new Map<string | null, HTMLButtonElement>()
  toolButtons.set(null, selectBtn)
  for (const t of DRAW_TOOLS) {
    const b = el('button', 'draw-tool', t.label)
    b.dataset.tool = t.type
    drawBar.append(b)
    toolButtons.set(t.type, b)
  }
  const delBtn = el('button', 'draw-tool', '🗑 削除')
  const clearAllBtn = el('button', 'draw-tool', '全消去')
  drawBar.append(delBtn, clearAllBtn)
  chartEl.append(drawBar)

  function highlightTool() {
    for (const [type, btn] of toolButtons) btn.classList.toggle('active', type === activeTool)
  }
  function pickTool(type: string | null) {
    activeTool = activeTool === type ? null : type
    chart.setDrawTool(activeTool)
    highlightTool()
  }
  selectBtn.onclick = () => pickTool(null)
  for (const t of DRAW_TOOLS) toolButtons.get(t.type)!.onclick = () => pickTool(t.type)
  delBtn.onclick = () => chart.deleteSelectedDrawing()
  clearAllBtn.onclick = () => {
    if (confirm('この銘柄のラインを全消去しますか？')) chart.clearDrawings()
  }
  drawBtn.onclick = () => {
    const open = drawBar.classList.toggle('open')
    if (!open) {
      activeTool = null
      chart.setDrawTool(null)
      highlightTool()
    }
  }

  // after a tool finishes placing, revert the toolbar to select mode (TV-style)
  chart.onPlacementDone(() => {
    activeTool = null
    highlightTool()
  })

  // keyboard: Delete/Backspace removes the selected drawing; Escape cancels placing
  window.addEventListener('keydown', (e) => {
    const ae = document.activeElement as HTMLElement | null
    if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable)) return
    if (e.key === 'Delete' || e.key === 'Backspace') {
      chart.deleteSelectedDrawing()
    } else if (e.key === 'Escape') {
      chart.cancelDrawing()
      activeTool = null
      highlightTool()
    }
  })

  // persist drawings (per dataset) on any change; restore them at startup
  chart.onDrawingsChanged(() => void gateway.saveDrawings(datasetKey, chart.exportDrawings()))
  if (savedDrawings.length) chart.importDrawings(savedDrawings)

  window.addEventListener('resize', () => chart.resize())

  rebuildTfOptions()
  const availTfs = ALL_TFS.filter(
    (tf) => TF_SECONDS[tf] >= TF_SECONDS[baseTf] && tfBarsFor(tf).length >= 25,
  )
  let savedTf: Timeframe | null = null
  try {
    savedTf = localStorage.getItem('lastTf') as Timeframe | null
  } catch {
    /* private mode */
  }
  const initialTf: Timeframe =
    savedTf && availTfs.includes(savedTf) ? savedTf : availTfs.includes('m15') ? 'm15' : baseTf
  startSession(initialTf)
}

boot()

// PWA: prompt to reload when a new version is deployed (never mid-session-silent).
const updateSW = registerSW({
  onNeedRefresh() {
    if (confirm('新しいバージョンがあります。更新しますか？')) void updateSW(true)
  },
})
